import { store, DEFAULTS } from "../state/index.ts";
import { live } from "../state/live.ts";
import { formatYearMonth } from "./utils.js";
import * as storage from "../services/storage.ts";
import * as saved_ from "../services/config.ts";
import * as api from "../services/api.ts";

// DOM elements
let form = null;
let sinceInput = null;
let untilInput = null;
let speedsSelect = null;
let ratingsSelect = null;
let ratingsSection = null;
let speedsSection = null;
let lichessUsernameInput = null;
let engineToggle = null;
let engineSourceSelect = null;
let engineUrlInput = null;
let engineDepthInput = null;
let engineMultiPVInput = null;
let autoPlayMode = null;
let boardSideSelect = null;
let configTriggerText = null;

// Callbacks
let onBoardOrientationChange = null;

export function initConfig(elements, callbacks = {}) {
    form = elements.form;
    sinceInput = elements.sinceInput;
    untilInput = elements.untilInput;
    speedsSelect = elements.speedsSelect;
    ratingsSelect = elements.ratingsSelect;
    ratingsSection = elements.ratingsSection;
    speedsSection = elements.speedsSection;
    lichessUsernameInput = elements.lichessUsernameInput;
    engineToggle = elements.engineToggle;
    engineSourceSelect = elements.engineSourceSelect;
    engineUrlInput = elements.engineUrlInput;
    engineDepthInput = elements.engineDepthInput;
    engineMultiPVInput = elements.engineMultiPVInput;
    autoPlayMode = elements.autoPlayMode;
    boardSideSelect = elements.boardSideSelect;
    configTriggerText = elements.configTriggerText;
    onBoardOrientationChange = callbacks.onBoardOrientationChange;

    // Set default date range
    const today = new Date();
    const yearAgo = new Date();
    yearAgo.setFullYear(today.getFullYear() - 2);
    if (sinceInput) sinceInput.value = formatYearMonth(yearAgo);
    if (untilInput) untilInput.value = formatYearMonth(today);

    // Initialize ratings visibility based on sparring skill
    updateRatingsVisibility();
}

export function setMultiSelect(selectEl, values) {
    if (!selectEl || !values) return;
    const set = new Set(values);
    // A <select multiple>, or (on /settings since 2026-11-01) a group of checkboxes sharing its name
    if (selectEl.options) {
        Array.from(selectEl.options).forEach((opt) => {
            opt.selected = set.has(opt.value);
        });
        return;
    }
    selectEl.querySelectorAll('input[type="checkbox"]').forEach((box) => {
        box.checked = set.has(box.value);
    });
}

// The form is the source of truth on the page that has it; everywhere else the saved configuration
// is. Both answer the same questions, so the rest of the app never has to know which page it is on.
// What "saved" means, its defaults and its sections, is services/config.ts — this module is the
// form around it.
function saved() {
    return saved_.stored();
}

export function collectSparringConfig() {
    if (!form) {
        return { ...saved_.sparring(), sparringSkill: getSparringSkill() };
    }
    const data = new FormData(form);
    return {
        since: data.get("since"),
        until: data.get("until"),
        speeds: data.getAll("speeds"),
        ratings: data.getAll("ratings"),
        sparringSkill: getSparringSkill(),
    };
}

export function collectEngineConfig() {
    if (!engineToggle) return saved_.engine();
    return {
        engineEnabled: engineToggle?.checked || false,
        engineSource: engineSourceSelect?.value || "browser",
        engineUrl: engineUrlInput?.value || "",
        engineDepth: engineDepthInput?.value || "20",
        engineMultiPV: engineMultiPVInput?.value === 0 ? 0 : engineMultiPVInput?.value || "1",
    };
}

export function collectPlayerConfig() {
    return { lichessUsername: getLichessUsername() };
}

export function collectBoardConfig() {
    return {
        autoPlayEnabled: isAutoPlayEnabled(),
        boardSide: boardSideSelect?.value || saved_.board().boardSide,
    };
}

export function collectConfig() {
    const sparring = collectSparringConfig();
    const engine = collectEngineConfig();
    const player = collectPlayerConfig();
    const board = collectBoardConfig();
    return {
        ...sparring,
        ...engine,
        sparringSkill: sparring.sparringSkill,
        player: player.lichessUsername,
        autoPlay: board.autoPlayEnabled,
        boardSide: board.boardSide,
    };
}

/**
 * The sparring filters as FormData, which is what the book query is built from. Without the form it
 * is rebuilt from the saved configuration, so a page without settings still queries the same book.
 */
export function getFormData() {
    if (form) return new FormData(form);
    const cfg = collectSparringConfig();
    const data = new FormData();
    data.set("since", cfg.since ?? "");
    data.set("until", cfg.until ?? "");
    (cfg.speeds || []).forEach((speed) => data.append("speeds", speed));
    (cfg.ratings || []).forEach((rating) => data.append("ratings", rating));
    return data;
}

export function getSparringSkill() {
    const selectedRadio = document.querySelector('input[name="sparringSkill"]:checked');
    return selectedRadio?.value || saved_.sparring().sparringSkill;
}

export function isMasterMode() {
    return getSparringSkill() === "master";
}

export function updateRatingsVisibility() {
    const isMaster = isMasterMode();
    // Hide ratings and speeds sections when in master mode
    const elementsToToggle = [ratingsSection, speedsSection];
    elementsToToggle.forEach((el) => {
        if (!el) return;
        if (isMaster) {
            el.classList.add("hidden");
        } else {
            el.classList.remove("hidden");
        }
    });
}

export function getSelectedBoardSide() {
    // Primary source: store (set by flip button, line loading, etc.)
    const storeSide = store.get('board.userSide');
    if (storeSide === "white" || storeSide === "black") {
        return storeSide;
    }
    // Fallback: the form select where there is one, the saved side otherwise
    if (boardSideSelect) {
        const normalized = (boardSideSelect.value || "").toLowerCase();
        if (normalized === "white" || normalized === "black") {
            return normalized;
        }
    }
    return DEFAULTS.boardSide;
}

export function isEngineEnabled() {
    return engineToggle ? engineToggle.checked : saved_.engine().engineEnabled;
}

export function isAutoPlayEnabled() {
    return autoPlayMode ? autoPlayMode.value === "true" : saved_.board().autoPlay;
}

export function getEngineSource() {
    return engineSourceSelect?.value || saved_.engine().engineSource;
}

export function getEngineUrl() {
    return engineUrlInput?.value?.trim() || saved_.engine().engineUrl;
}

export function getEngineDepth() {
    return parseInt(engineDepthInput?.value, 10) || saved_.engine().engineDepth;
}

export function getEngineMultiPV() {
    if (!engineMultiPVInput) return saved_.engine().engineMultiPV;
    const value = parseInt(engineMultiPVInput.value, 10);
    return value === 0 ? 0 : value || 1;
}

export function getLichessUsername() {
    return lichessUsernameInput?.value?.trim() || saved_.player().lichessUsername;
}

export function updateBoardOrientation() {
    const ground = live.ground;
    if (!ground) {
        return;
    }
    ground.set({ orientation: getSelectedBoardSide() });
    if (onBoardOrientationChange) {
        onBoardOrientationChange(getSelectedBoardSide());
    }
}

export function applySparringConfig(configuration) {
    if (!configuration) {
        return;
    }
    if (configuration.since && sinceInput) {
        sinceInput.value = configuration.since;
    }
    if (configuration.until && untilInput) {
        untilInput.value = configuration.until;
    }
    if (Array.isArray(configuration.speeds)) {
        setMultiSelect(speedsSelect, configuration.speeds);
    }
    if (Array.isArray(configuration.ratings)) {
        setMultiSelect(ratingsSelect, configuration.ratings);
    }
    if (configuration.sparringSkill) {
        const radioToSelect = document.querySelector(`input[name="sparringSkill"][value="${configuration.sparringSkill}"]`);
        if (radioToSelect) {
            radioToSelect.checked = true;
            updateRatingsVisibility();
        }
    }
}

export function applyEngineConfig(configuration) {
    if (!configuration) {
        return;
    }
    if (engineToggle && typeof configuration.engineEnabled === "boolean") {
        engineToggle.checked = configuration.engineEnabled;
    }
    if (engineSourceSelect && configuration.engineSource) {
        engineSourceSelect.value = configuration.engineSource;
    }
    if (engineUrlInput && configuration.engineUrl !== undefined) {
        engineUrlInput.value = configuration.engineUrl;
    }
    if (engineDepthInput && configuration.engineDepth !== undefined) {
        engineDepthInput.value = configuration.engineDepth;
    }
    if (engineMultiPVInput && configuration.engineMultiPV !== undefined) {
        engineMultiPVInput.value = configuration.engineMultiPV;
    }
}

export function applyPlayerConfig(configuration) {
    if (!configuration) {
        return;
    }
    if (lichessUsernameInput && configuration.lichessUsername !== undefined) {
        lichessUsernameInput.value = configuration.lichessUsername || "";
    }
}

export function applyBoardConfig(configuration) {
    if (!configuration) {
        return;
    }
    if (autoPlayMode && typeof configuration.autoPlayEnabled === "boolean") {
        autoPlayMode.value = configuration.autoPlayEnabled ? "true" : "false";
        persistAutoPlay();
    }
    if (boardSideSelect && configuration.boardSide) {
        boardSideSelect.value = configuration.boardSide;
    }
    updateBoardOrientation();
}

function getDefaultSparringConfig() {
    const today = new Date();
    const yearAgo = new Date();
    yearAgo.setFullYear(today.getFullYear() - 2);
    return {
        since: formatYearMonth(yearAgo),
        until: formatYearMonth(today),
        speeds: [...DEFAULTS.speeds],
        ratings: [...DEFAULTS.ratings],
    };
}

function getDefaultEngineConfig() {
    return {
        engineEnabled: DEFAULTS.engineEnabled,
        engineSource: DEFAULTS.engineSource,
        engineUrl: "",
        engineDepth: DEFAULTS.engineDepth,
        engineMultiPV: DEFAULTS.engineMultiPV,
    };
}

function getDefaultPlayerConfig() {
    return {
        lichessUsername: "",
    };
}

function getDefaultBoardConfig() {
    return {
        autoPlayEnabled: true,
        boardSide: DEFAULTS.boardSide,
    };
}

export function applyDefaultConfig() {
    store.set('config.applyingStoredConfig', true);
    try {
        applySparringConfig(getDefaultSparringConfig());
        applyEngineConfig(getDefaultEngineConfig());
        applyPlayerConfig(getDefaultPlayerConfig());
        applyBoardConfig(getDefaultBoardConfig());
    } finally {
        store.set('config.applyingStoredConfig', false);
    }
    syncLocalStorageFromUi();
    updateConfigSummary();
}

export function applyUserConfiguration(config, callbacks = {}) {
    if (!config) {
        return;
    }
    store.set('config.applyingStoredConfig', true);
    try {
        applySparringConfig(config.sparringConfig);
        applyEngineConfig(config.engineConfig);
        applyPlayerConfig(config.playerConfig);
        applyBoardConfig(config.boardConfig);
        // What each tool keeps for itself has no form: the account's copy simply wins
        if (config.toolConfig) saved_.patch({ tools: config.toolConfig });
    } finally {
        store.set('config.applyingStoredConfig', false);
    }
    syncLocalStorageFromUi();
    updateConfigSummary();
    if (callbacks.onApplied) {
        callbacks.onApplied();
    }
}

export function syncLocalStorageFromUi() {
    try {
        saved_.patch(collectConfig());
        persistAutoPlay();
    } catch (error) {
        console.warn("Failed to update stored configuration", error);
    }
}

export async function saveConfig() {
    const applyingStoredConfig = store.get('config.applyingStoredConfig');
    if (applyingStoredConfig) return;
    saved_.patch(collectConfig());
    await persistUserConfig();
}

export async function persistUserConfig() {
    const currentUser = store.get('user.currentUser');
    const applyingStoredConfig = store.get('config.applyingStoredConfig');
    if (!currentUser || applyingStoredConfig) {
        return;
    }
    const payload = {
        sparringConfig: collectSparringConfig(),
        engineConfig: collectEngineConfig(),
        playerConfig: collectPlayerConfig(),
        boardConfig: collectBoardConfig(),
        toolConfig: saved_.stored().tools || {},
    };
    try {
        await api.saveUserConfig(payload);
    } catch (error) {
        console.error("Failed to persist user configuration", error);
    }
}

export function loadStoredConfig(callbacks = {}) {
    const cfg = saved_.stored();
    if (!Object.keys(cfg).length) {
        applyDefaultConfig();
        return;
    }
    store.set('config.applyingStoredConfig', true);
    try {
        if (cfg.since && sinceInput) sinceInput.value = cfg.since;
        if (cfg.until && untilInput) untilInput.value = cfg.until;
        setMultiSelect(speedsSelect, cfg.speeds || []);
        setMultiSelect(ratingsSelect, cfg.ratings || []);
        if (cfg.sparringSkill) {
            const radioToSelect = document.querySelector(`input[name="sparringSkill"][value="${cfg.sparringSkill}"]`);
            if (radioToSelect) {
                radioToSelect.checked = true;
                updateRatingsVisibility();
            }
        }
        if (engineToggle && typeof cfg.engineEnabled === "boolean") {
            engineToggle.checked = cfg.engineEnabled;
        }
        if (engineSourceSelect && cfg.engineSource) {
            engineSourceSelect.value = cfg.engineSource;
        }
        if (engineUrlInput && cfg.engineUrl !== undefined) {
            engineUrlInput.value = cfg.engineUrl;
        }
        if (engineDepthInput && cfg.engineDepth !== undefined) {
            engineDepthInput.value = cfg.engineDepth;
        }
        if (engineMultiPVInput && cfg.engineMultiPV !== undefined) {
            engineMultiPVInput.value = cfg.engineMultiPV;
        }
        if (lichessUsernameInput && cfg.player !== undefined) {
            lichessUsernameInput.value = cfg.player || "";
        }
        if (boardSideSelect && cfg.boardSide) {
            boardSideSelect.value = cfg.boardSide;
        }
        if (autoPlayMode && cfg.autoPlay !== undefined) {
            autoPlayMode.value = cfg.autoPlay ? "true" : "false";
            persistAutoPlay();
        }
        updateBoardOrientation();
    } finally {
        store.set('config.applyingStoredConfig', false);
    }
    updateConfigSummary();
}

export function updateConfigSummary() {
    const sparring = collectSparringConfig();
    const skill = sparring.sparringSkill;

    let levelText;
    if (skill === "master") {
        levelText = "Masters";
    } else {
        const ratings = (sparring.ratings || []).filter(Boolean);
        levelText = ratings.length ? ratings.join("-") : "Amateur";
    }

    const speeds = (sparring.speeds || []).filter(Boolean);
    const speedText = speeds.length ? speeds.join(", ") : "all speeds";
    const summary = `Against ${levelText} ${speedText} lichess`;

    store.set('config.sparringSummary', summary);

    // Compact summary for header button
    if (configTriggerText) {
        if (skill === "master") {
            configTriggerText.textContent = "Masters";
        } else {
            const ratings = (sparring.ratings || []).filter(Boolean).join("-");
            configTriggerText.textContent = ratings ? `${levelText}` : levelText;
        }
    }
}

export function persistCollapseState() {
    // No longer used - dropdown doesn't persist state
}

export function restoreCollapseState() {
    // No longer used - dropdown doesn't persist state
}

export function restoreAutoPlay() {
    if (!autoPlayMode) return;
    const state = storage.loadAutoPlay();
    if (state === "true" || state === "false") {
        autoPlayMode.value = state;
    }
}

export function persistAutoPlay() {
    if (!autoPlayMode) return;
    storage.saveAutoPlay(autoPlayMode.value === "true");
}
